/**
 * Unit 1.4 · IMPL.B: unified Add/Edit client dialog.
 *
 * Bead: insurescope-11zkc.2.4.3.2. Design contract:
 * docs/design-briefs/2026-06-03-clients-list.md (ClientFormDialog section).
 *
 * Form state seeds on `[open, client?.id]` ONLY; a background refetch that
 * hands down a new `client` object identity (same id) must never clobber
 * in-progress edits (locked by ClientFormDialog.test.tsx).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
    createClient,
    getApiErrorMessage,
    updateClient,
    type IApiAxiosError,
    type IClient,
} from "@/api";
import { IndustryMultiCombobox } from "@/components/shared/IndustryMultiCombobox";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface ClientFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Present = Edit mode (prefilled); absent/null = Add mode. */
    client?: IClient | null;
}

export function ClientFormDialog({
    open,
    onOpenChange,
    client,
}: ClientFormDialogProps) {
    const queryClient = useQueryClient();
    const isEdit = Boolean(client);

    const [name, setName] = useState("");
    const [legalName, setLegalName] = useState("");
    const [industryIds, setIndustryIds] = useState<number[]>([]);
    const [apiError, setApiError] = useState<string | null>(null);

    // Seed exactly once per open/target, keyed on the id, NOT the object
    // identity, so refetches can't reset the user's typing mid-edit.
    const clientId = client?.id;
    useEffect(() => {
        if (!open) return;
        if (client) {
            setName(client.name);
            setLegalName(client.legal_name ?? "");
            setIndustryIds(client.industries.map((i) => i.id));
        } else {
            setName("");
            setLegalName("");
            setIndustryIds([]);
        }
        setApiError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- seed-once contract
    }, [open, clientId]);

    const { mutate, isPending } = useMutation({
        mutationFn: () => {
            const trimmedName = name.trim();
            const trimmedLegalName = legalName.trim();
            if (client) {
                // Tri-state on edit: a cleared field sends null (clear).
                return updateClient(client.id, {
                    name: trimmedName,
                    legal_name: trimmedLegalName || null,
                    industry_ids: industryIds,
                });
            }
            // Blank legal name is omitted on create, never sent as "".
            return createClient({
                name: trimmedName,
                legal_name: trimmedLegalName || undefined,
                industry_ids: industryIds,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["clients"] });
            queryClient.invalidateQueries({ queryKey: ["clients-stats"] });
            toast.success(isEdit ? "Client updated" : "Client added");
            onOpenChange(false);
        },
        onError: (err: IApiAxiosError) => {
            setApiError(
                getApiErrorMessage(
                    err,
                    isEdit ? "Failed to update client" : "Failed to add client"
                )
            );
        },
    });

    const handleSubmit = () => {
        if (!name.trim() || isPending) return;
        setApiError(null);
        mutate();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit ? "Edit Client" : "Add Client"}
                    </DialogTitle>
                    {!isEdit && (
                        <DialogDescription>
                            Group documents, scopes, and Polly chats under a
                            single client workspace.
                        </DialogDescription>
                    )}
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="client-form-name">
                            Client name
                            <span className="text-destructive ml-0.5">*</span>
                        </Label>
                        <Input
                            id="client-form-name"
                            autoFocus
                            value={name}
                            placeholder="e.g. Coastal Logistics Group"
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSubmit();
                            }}
                        />
                        <p className="text-muted-foreground text-xs">
                            How this client appears throughout the app.
                        </p>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="client-form-legal-name">
                            Legal name
                        </Label>
                        <Input
                            id="client-form-legal-name"
                            value={legalName}
                            placeholder="e.g. Coastal Logistics Group, LLC"
                            onChange={(e) => setLegalName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSubmit();
                            }}
                        />
                        <p className="text-muted-foreground text-xs">
                            The full legal entity name as it appears on policies
                            and contracts. Helps automatically match uploaded
                            documents to this client.
                        </p>
                    </div>

                    <IndustryMultiCombobox
                        value={industryIds}
                        onChange={setIndustryIds}
                        label="Industries"
                    />

                    {apiError && (
                        <p className="text-destructive text-sm">{apiError}</p>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!name.trim() || isPending}
                    >
                        {isPending && (
                            <Loader2 className="size-4 animate-spin" />
                        )}
                        {isEdit ? "Save Changes" : "Add Client"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
